import { FetchHelper } from "shared-ts-utils-fetch";
import { SettingKey } from "../SettingKeyDto";
import { AsyncSettingsStorage } from "./AsyncSettingsStorage";
import { AppKey } from "shared-ts-app-info";
import { UserSettingDto } from "./UserSetting";
import { AccessToken } from "shared-ts-utils-authentication";
import { IllegalArgumentError } from "shared-ts-utils-core";
import { SettingConfig } from "../SettingConfig";
import { SettingOverrides } from "../SettingOverrides";

export interface ServerStorageOptions {
  baseApiPath?: string;
  accessToken?: AccessToken;
}

export class ServerSettingsStorage implements AsyncSettingsStorage {
  public readonly type: "Async";
  private readonly fetchHelper;

  public constructor(options?: ServerStorageOptions) {
    this.fetchHelper = new FetchHelper({
      basePath: options?.baseApiPath ?? "/api/user-settings/",
    });
    this.fetchHelper.authorizationBearer(options?.accessToken);
    this.type = "Async";
  }

  private checkValidSettingKey(key: SettingKey) {
    if (key.app == null) {
      throw new IllegalArgumentError(
        "ServerSettingsStorage does not support settings without an app key: " +
          key.asString
      );
    }
  }
  loadSettings(
    appKey: AppKey | null | undefined,
    user: string | null | undefined
  ): Promise<Array<UserSettingDto>> {
    if (appKey == null) {
      throw new IllegalArgumentError(
        "ServerSettingsStorage does not support settings without an app key"
      );
    }
    return this.fetchHelper
      .get(`load?app=${appKey}&user=${user}`)
      .fetchAsJson();
  }

  async readValue<Type>(
    key: SettingKey,
    config: SettingConfig<Type>,
    options?: SettingOverrides<Type>
  ): Promise<Type> {
    this.checkValidSettingKey(key);
    const value = await this.fetchHelper
      .get(`get?app=${key.app}&user=${key.user}&name=${key.name}`)
      .fetchAsJson<string | null>();

    const valueDeserialized =
      value != null
        ? options?.deserialize != null
          ? options.deserialize(value)
          : config.deserialize(value)
        : null;

    return Promise.resolve(valueDeserialized as Type);
  }

  writeValue<Type>(
    key: SettingKey,
    newValue: Type,
    config: SettingConfig<Type>,
    overrides?: SettingOverrides<Type>
  ): Promise<void> {
    this.checkValidSettingKey(key);
    const newValueSerialized =
      newValue != null
        ? overrides?.serialize != null
          ? overrides.serialize(newValue)
          : config.serialize(newValue)
        : null;

    return this.fetchHelper
      .post("set", {
        app: key.app,
        user: key.user,
        name: key.name,
        value: newValueSerialized,
        type: config.type,
      })
      .fetch();
  }
}
